import * as React from 'react'

import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'

import { PermitFieldType } from '~/client/graph/types.generated'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { DragIcon } from '~/client/src/shared/components/icons/DragIcon'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FormFieldsModal from '../FormFieldsModal'
import FieldMappingScreenStore, {
  IImportedField,
} from './FieldMappingScreen.store'
import StepSelect from './StepSelect'

interface IProps {
  field: IImportedField
  store: FieldMappingScreenStore
}

function ImportedFieldRow({ field, store }: IProps): JSX.Element {
  const effectiveType = store.getEffectiveType(field.id)
  const effectiveCaption = store.getEffectiveCaption(field.id)
  const hasOverride = store.fieldTypeOverrides.has(field.id)
  const disabledFields = store.getDisabledFieldTypes(field.id)
  const effectiveTypeCaption = getFieldTypeCaption(effectiveType)

  const [isEditing, setIsEditing] = React.useState(false)
  const [editValue, setEditValue] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleFieldTypeChange = (fieldType: PermitFieldType) => {
    store.reassignFieldType(field.id, fieldType)
  }

  const startEditing = () => {
    setEditValue(effectiveCaption)
    setIsEditing(true)
  }

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const commitEdit = () => {
    const trimmed = editValue.trim()
    if (trimmed) {
      store.setCaptionOverride(field.id, trimmed)
    }
    setIsEditing(false)
  }

  const cancelEdit = () => {
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  return (
    <div className="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-2.5 tw:border-b tw:border-border tw:last:border-b-0 tw:hover:bg-muted/30 tw:transition-colors tw:bg-background">
      <div className="tw:shrink-0">
        <DragIcon size="sm" className="tw:text-muted-foreground tw:shrink-0" />
      </div>

      <div className="tw:flex-1 tw:min-w-0">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            className="tw:h-7 tw:text-sm"
          />
        ) : (
          <div className="tw:flex tw:items-center tw:gap-1.5 tw:group/label">
            <span className="tw:text-sm tw:font-medium tw:text-foreground tw:truncate">
              {effectiveCaption}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              className="tw:opacity-0 tw:pointer-events-none tw:group-[&:hover]/label:opacity-100 tw:group-[&:hover]/label:pointer-events-auto tw:focus-visible:opacity-100 tw:transition-opacity tw:text-muted-foreground tw:shrink-0"
              aria-label={Localization.translator.rename}
              onClick={startEditing}
            >
              <Pencil size={14} />
            </Button>
            {hasOverride && (
              <span className="tw:text-[10px] tw:text-muted-foreground tw:italic tw:shrink-0">
                changed
              </span>
            )}
          </div>
        )}
      </div>

      <div className="tw:shrink-0">
        <FormFieldsModal
          addNewField={handleFieldTypeChange}
          disabledFields={disabledFields}
        >
          <FieldTrigger
            aria-label={`${Localization.translator.type}: ${effectiveTypeCaption}`}
          >
            <span className="tw:flex tw:items-center tw:gap-1.5">
              <PermitFieldIcon fieldName={effectiveType} />
              <span className="tw:truncate tw:max-w-32">
                {effectiveTypeCaption}
              </span>
            </span>
          </FieldTrigger>
        </FormFieldsModal>
      </div>

      <div className="tw:shrink-0">
        <StepSelect fieldId={field.id} store={store} />
      </div>

      <div className="tw:shrink-0">
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:text-muted-foreground tw:hover:text-destructive"
          onClick={() => store.showDeleteConfirmation(field.id)}
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  )
}

export default observer(ImportedFieldRow)
